import React, { PropsWithChildren } from 'react';
import { Else, If, Then, When } from 'react-if';
import { useEffectOnce } from 'react-use';
// eslint-disable-next-line no-restricted-imports -- 기존 사용처. overlay-kit 마이그레이션은 별건으로 진행한다.
import { useModal } from '@ebay/nice-modal-react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { PersonStackFillIcon } from '@zep/icons';
import { useViewportDevice } from '@zep/react';
import { PlanType } from '@zep/types';
import { Button, Stack } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { SubscribePlan } from '../../../../types';
import { PlanTemplateType } from '../../plan-payment';
import { PlanTypeLabel, SubscriptionRejectedFallback } from '../components';
import { capitalizeFirstLetter } from '../subscriptionUtil';
import { PlanApplyFeature } from './components';
import { SubscriptionPaymentLogPayload, useSubscriptionPaymentLog } from './hooks';
import { PhoneNumberInputDialog } from './modals';
import S from './PlanCheckoutComplete.module.scss';
import { PaymentUpdateStatusType } from './SubscriptionPaymentProvider';
export const PaymentComplete = () => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['subscription']);
  const phoneNumberInputModal = useModal(PhoneNumberInputDialog);
  const {
    title = '',
    orderId = '',
    thumbnailUrl = '',
    entryMapHashId = '',
    spaceName = '',
    plan = 'basic',
    maxCcu = 0,
    spaceHashId = '',
    updateStatus: _updateStatus = '',
    updateMessages = '',
    amplitudeData: stringifyAmpleData,
    subscriptionPeriod
  } = router.query;
  const send = useSubscriptionPaymentLog('payment_subscription_purchase_complete', true, subscriptionPeriod === '365' ? 'Annual' : 'Monthly');
  const amplitudeData: SubscriptionPaymentLogPayload = (() => {
    try {
      return JSON.parse((stringifyAmpleData || '').toString());
    } catch {
      return undefined;
    }
  })();
  const updateStatus = _updateStatus as PaymentUpdateStatusType;
  useEffectOnce(() => {
    if (amplitudeData) {
      send(amplitudeData);
    }
  });
  useEffectOnce(() => {
    phoneNumberInputModal.show({
      spaceHashId: String(spaceHashId),
      planTemplateType: PlanTemplateType.PRODUCT_SUBSCRIPTION
    });
  });

  // 뒤로가기나 잘못된 접근이 안되게 변경 합니다.
  if (!thumbnailUrl) {
    return <SubscriptionRejectedFallback />;
  }
  return <>
      <div className={S.container}>
        <Stack className={S.wrapper} style={{
        width: 716
      }} data-sentry-element="Stack" data-sentry-source-file="PaymentComplete.tsx">
          <div className={S.inner}>
            <Title data-sentry-element="Title" data-sentry-source-file="PaymentComplete.tsx">{title.toString()}</Title>
            <div className={S.content}>
              <SpaceInfo maxCcu={maxCcu.toString()} plan={plan.toString().toUpperCase() as PlanType} thumbnailUrl={thumbnailUrl.toString()} spaceName={spaceName.toString()} data-sentry-element="SpaceInfo" data-sentry-source-file="PaymentComplete.tsx" />
              <section className={S.content_right}>
                <When condition={!!orderId} data-sentry-element="When" data-sentry-source-file="PaymentComplete.tsx">
                  <div className={S.order_id_title}>
                    {t('subscription:payment.complete.orderNumber')}
                    {orderId}
                  </div>
                </When>
                <div className={S.benefit_info}>
                  <NoticeText spaceName={spaceName.toString()} plan={capitalizeFirstLetter(plan.toString())} status={updateStatus} data-sentry-element="NoticeText" data-sentry-source-file="PaymentComplete.tsx" />
                  <If condition={updateStatus === 'DOWN_GRADE'} data-sentry-element="If" data-sentry-source-file="PaymentComplete.tsx">
                    <Then data-sentry-element="Then" data-sentry-source-file="PaymentComplete.tsx">
                      <div className={S.update_message}>{updateMessages}</div>
                      <PlanApplyFeature title={Number(maxCcu) < 0 ? t('subscription:SpaceDetail.menuManage.currentPlanMaxCcu') : t('subscription:SpaceDetail.menuManage.currentPlanCcu', {
                      ccu: maxCcu
                    })} plan={plan.toString() as SubscribePlan} isDowngrade data-sentry-element="PlanApplyFeature" data-sentry-source-file="PaymentComplete.tsx" />
                    </Then>
                    <Else data-sentry-element="Else" data-sentry-source-file="PaymentComplete.tsx">
                      <PlanApplyFeature title={Number(maxCcu) < 0 ? t('subscription:SpaceDetail.menuManage.currentPlanMaxCcu') : t('subscription:SpaceDetail.menuManage.currentPlanCcu', {
                      ccu: maxCcu
                    })} plan={plan.toString() as SubscribePlan} data-sentry-element="PlanApplyFeature" data-sentry-source-file="PaymentComplete.tsx" />
                      <div className={S.update_message}>{updateMessages}</div>
                    </Else>
                  </If>
                </div>
              </section>
            </div>
          </div>
          <div className={S.footer}>
            <ActionButtons spaceHashId={spaceHashId.toString()} entryMapHashId={entryMapHashId.toString()} data-sentry-element="ActionButtons" data-sentry-source-file="PaymentComplete.tsx" />
          </div>
        </Stack>
      </div>

      <style global jsx>{`
        body {
          background-color: ${Colors.Gray50};
        }
      `}</style>
    </>;
};
const Title = ({
  children
}: PropsWithChildren) => {
  return <div className={S.title} data-sentry-component="Title" data-sentry-source-file="PaymentComplete.tsx">{children}</div>;
};
const NoticeText = ({
  spaceName,
  plan,
  status
}: {
  spaceName: string;
  plan: string;
  status: PaymentUpdateStatusType;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  return <div className={S.notice} data-sentry-component="NoticeText" data-sentry-source-file="PaymentComplete.tsx">
      <If condition={status === 'DOWN_GRADE'} data-sentry-element="If" data-sentry-source-file="PaymentComplete.tsx">
        <Then data-sentry-element="Then" data-sentry-source-file="PaymentComplete.tsx">{t('subscription:payment.complete.downgradeNotice')}</Then>
        <Else data-sentry-element="Else" data-sentry-source-file="PaymentComplete.tsx">
          <div className={S.notice_text}>
            {t('subscription:payment.complete.normalNotice1')}
          </div>
          <div className={S.notice_text}>
            {t('subscription:payment.complete.normalNotice2', {
            spaceName: `[${spaceName}]`,
            plan: plan
          })}
          </div>
        </Else>
      </If>
    </div>;
};
const SpaceInfo = ({
  thumbnailUrl,
  plan,
  spaceName,
  maxCcu
}: {
  thumbnailUrl: string;
  plan: PlanType;
  spaceName: string;
  maxCcu: string;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  return <section className={S.content_left} data-sentry-component="SpaceInfo" data-sentry-source-file="PaymentComplete.tsx">
      <div className={S.thumbnail}>
        <img src={thumbnailUrl} alt="thumbnail" />
      </div>
      <Stack gap={6} data-sentry-element="Stack" data-sentry-source-file="PaymentComplete.tsx">
        <PlanTypeLabel planType={plan} data-sentry-element="PlanTypeLabel" data-sentry-source-file="PaymentComplete.tsx" />
        <div className={S.plan_ccu}>
          <PersonStackFillIcon width={16} height={16} fill={Colors.Gray500} data-sentry-element="PersonStackFillIcon" data-sentry-source-file="PaymentComplete.tsx" />
          {Number(maxCcu) < 0 ? t('subscription:checkout.complete.maxCcuInfinite') : t('subscription:payment.complete.ccuPerson', {
          ccu: maxCcu
        })}
        </div>
        <div className={S.space_name}>{spaceName}</div>
      </Stack>
    </section>;
};
const ActionButtons = ({
  spaceHashId,
  entryMapHashId
}: {
  spaceHashId: string;
  entryMapHashId: string;
}) => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['subscription']);
  const {
    isPortraitMobile
  } = useViewportDevice();
  return <div className={S.buttons} data-sentry-component="ActionButtons" data-sentry-source-file="PaymentComplete.tsx">
      <Button variant={'sub'} size={isPortraitMobile ? 'md' : 'lg'} isFullWidth={isPortraitMobile} onClick={() => router.replace(`/me/spaces/${spaceHashId}/plan`)} data-sentry-element="Button" data-sentry-source-file="PaymentComplete.tsx">
        {t('subscription:payment.complete.manageSubscription')}
      </Button>
      <Button size={isPortraitMobile ? 'md' : 'lg'} isFullWidth={isPortraitMobile} onClick={() => router.replace(`/play/${entryMapHashId}`)} data-sentry-element="Button" data-sentry-source-file="PaymentComplete.tsx">
        {t('subscription:payment.complete.goSpace')}
      </Button>
    </div>;
};